// composables/useSharedCursor.ts
import { ref, onMounted, onUnmounted, watch, type Ref } from 'vue';
import { io, Socket } from "socket.io-client";
import type { DefaultEventsMap } from '@socket.io/component-emitter';
import { v4 as uuidv4 } from 'uuid';
import cursorImg from '@/public/cursor.svg'; // Passe den Pfad bei Bedarf an, wenn er sich ändert

interface PointerCoordinates {
    id: string;
    name: string;
    x: string;
    y: string;
    room: string; // Added room property
}

// Map to keep track of currently active cursor DOM elements
// This helps to quickly check if a cursor already exists and get a direct reference
const activeCursorElements = new Map<string, HTMLElement>();

export function useSharedCursor(
    socketServer: string, // This will be your Render domain (e.g., "https://your-combined-app.onrender.com")
    visitorName: Ref<string>,
    functionalCookies: Ref<boolean>, // Pass functionalCookies to control socket connection
    currentPagePath: Ref<string> // New: Pass the current page path as room ID
) {
    const loggedUsers = ref<string[]>([]); // Keep for tracking logged in users (optional, but good for overview)
    const uuid = uuidv4(); // Unique UUID for this client
    const socket = ref<Socket<DefaultEventsMap, DefaultEventsMap> | null>(null);

    // Variables for optimized mouse position sending
    let animationFrameId: number | null = null; // For sending updates
    let lastMousePos: { clientX: number; clientY: number; } | null = null;
    let lastSentTime = 0;
    // Set send interval to 16ms for approximately 60 FPS updates.
    const SEND_INTERVAL = 16; 

    // Helper function to emit socket messages
    const socketEmit = (topic: string, msg: any) => {
        socket.value?.emit(topic, msg);
    };

    // Helper function to subscribe to socket messages
    const socketSubscribe = (topic: string, callback: (...args: any[]) => void) => {
        socket.value?.on(topic, callback);
    };

    const createPointer = (userID: string, userName: string) => {
        // Check if cursor already exists in our map before creating
        if (activeCursorElements.has(userID)) {
            return;
        }

        // Add to loggedUsers array (for general tracking, not strictly for DOM management)
        if (loggedUsers.value.findIndex(obj => obj === userID) === -1) {
            loggedUsers.value.push(userID);
        }

        const fixedName = userName.replaceAll(' ', '-');

        const cursorContainer = document.createElement('div');
        cursorContainer.id = `cursor-${userID}`;
        cursorContainer.classList.add('user-cursor'); // Styling will come from this class

        // Set critical CSS properties directly via JavaScript with !important
        // This is to forcefully ensure visibility and positioning, overriding any external CSS.
        cursorContainer.style.setProperty('position', 'fixed', 'important');
        cursorContainer.style.setProperty('z-index', '9999', 'important');
        cursorContainer.style.setProperty('display', 'flex', 'important');
        cursorContainer.style.setProperty('align-items', 'center', 'important');
        cursorContainer.style.setProperty('gap', '5px', 'important');
        cursorContainer.style.setProperty('pointer-events', 'none', 'important');
        cursorContainer.style.setProperty('transition', 'none', 'important'); // No CSS transitions
        cursorContainer.style.setProperty('will-change', 'transform', 'important');
        cursorContainer.style.setProperty('opacity', '1', 'important'); // Ensure fully opaque
        cursorContainer.style.setProperty('visibility', 'visible', 'important'); // Ensure visible

        // Initial transform for immediate positioning (will be updated by movePointer)
        cursorContainer.style.transform = `translateX(0px) translateY(0px)`; 

        const cursor = document.createElement('img');
        cursor.src = cursorImg;

        const nameContainer = document.createElement('span');
        nameContainer.innerHTML = 'Hi&nbsp;there!';

        cursorContainer.append(cursor);
        cursorContainer.append(nameContainer);

        // Assert that document.body exists before appending
        if (document.body) {
            document.body.append(cursorContainer);
        } else {
            console.error("[createPointer] Cannot append cursor: document.body is not available.");
        }

        // Add the created element to our tracking map
        activeCursorElements.set(userID, cursorContainer);

        setTimeout(() => {
            nameContainer.innerHTML = fixedName;
        }, 1500);
    };

    const movePointer = (userID: string, x: number, y: number) => {
        const cursor = activeCursorElements.get(userID); // Get element from map
        
        if (!cursor) {
            return; // Cursor not found, cannot move
        }

        let xOffsetContainer = Number(x) + 40;
        let yOffsetContainer = Number(y) + 35;

        // Directly set transform for immediate movement
        cursor.style.transform = `translateX(${xOffsetContainer}px) translateY(${yOffsetContainer}px)`;
    };

    const removePointer = (userID: string) => {
        const leavingCursor = activeCursorElements.get(userID); // Get element from map
        
        if (!leavingCursor) {
            return;
        }

        const leavingText = leavingCursor.querySelector('span') as HTMLElement;

        if (leavingText) {
            leavingText.innerHTML = 'Good&nbsp;Bye!';
        }

        // Remove from map immediately
        activeCursorElements.delete(userID);

        // Remove from loggedUsers array
        loggedUsers.value = loggedUsers.value.filter(id => id !== userID);

        setTimeout(() => {
            leavingCursor?.remove();
        }, 1500);
    };

    const sendMousePos = (canvas: HTMLElement, clientX: number, clientY: number) => {
        let name = visitorName.value === '' ? 'UNKNOWN-USER' : visitorName.value.toUpperCase();
        let rect = canvas.getBoundingClientRect();
        let coordinates: PointerCoordinates = {
            id: uuid,
            name: name,
            x: ((clientX - rect.left) / rect.width * 100).toFixed(2),
            y: ((clientY - rect.top) / rect.height * 100).toFixed(2),
            room: currentPagePath.value // Include the current room
        };
        socketEmit("position", coordinates);
    };

    // This function is called repeatedly by requestAnimationFrame for sending
    const updateMousePositionAndSend = () => {
        if (lastMousePos && process.client && socket.value?.connected) {
            const currentTime = performance.now();
            // Only send if enough time has passed since the last send
            if (currentTime - lastSentTime >= SEND_INTERVAL) {
                sendMousePos(document.body, lastMousePos.clientX, lastMousePos.clientY);
                lastSentTime = currentTime;
            }
        }
        // Continue the animation frame loop for sending
        animationFrameId = requestAnimationFrame(updateMousePositionAndSend);
    };

    // Handler for raw mouse movement event
    const handleMouseMove = (e: MouseEvent) => {
        lastMousePos = { clientX: e.clientX, clientY: e.clientY };
        // Start the animation frame loop for sending if it's not already running
        // console.log(lastMousePos)
        if (!animationFrameId) {
            animationFrameId = requestAnimationFrame(updateMousePositionAndSend);
        }
    };

    // Initialize socket connection and subscribe to events
    const connectSocket = () => {
        if (!socket.value) { // Connect only if not already connected
            // *** DIESE ZEILE WURDE ANGEPASST ***
            // Die io() Funktion muss die Basis-URL des Render-Service erhalten
            // und der 'path'-Parameter muss den Unterpfad des Socket.IO-Servers angeben.
            socket.value = io(socketServer, {
                path: '/shared-cursors/', // Dieser Pfad MUSS mit dem Serverseitigen 'path' übereinstimmen
                transports: ['websocket', 'polling'] // Stellt sicher, dass WebSocket bevorzugt wird, Polling als Fallback
            });
            console.log("SOCKET CONNECTED");

            socketSubscribe("connect", () => {
                let name = visitorName.value === '' ? 'UNKNOWN-USER' : visitorName.value.toUpperCase();
                socketEmit("init", {
                    uuid: uuid,
                    name: name,
                    role: "master",
                    room: currentPagePath.value // Send initial room
                });
                // Start listening for mouse movement and sending updates
                window.addEventListener('mousemove', handleMouseMove);
                animationFrameId = requestAnimationFrame(updateMousePositionAndSend);
            });

            // Subscribe to 'userJoinedRoom' event to create cursors for new users
            socketSubscribe("userJoinedRoom", (data: { userID: string; userName: string; room: string }) => {
                // Ensure it's not our own user and they are in the current room
                if (data.userID !== uuid && data.room === currentPagePath.value) {
                    createPointer(data.userID, data.userName);
                    // Immediately request a position from the newly joined user
                    // This helps in getting an initial position quickly
                    socketEmit("requestPosition", { targetUserID: data.userID, room: currentPagePath.value });
                }
            });

            // Handle 'requestPosition' from other clients
            socketSubscribe("requestPosition", (data: { targetUserID: string; room: string }) => {
                if (data.targetUserID === uuid && data.room === currentPagePath.value) {
                    // Send current position immediately
                    if (lastMousePos) {
                        sendMousePos(document.body, lastMousePos.clientX, lastMousePos.clientY);
                    }
                }
            });

            // Handle 'sendMyPosition' from server (response to requestPosition)
            socketSubscribe("sendMyPosition", (data: { room: string }) => {
                if (process.client && lastMousePos && data.room === currentPagePath.value) {
                    sendMousePos(document.body, lastMousePos.clientX, lastMousePos.clientY);
                }
            });

            socketSubscribe("userPosition", (data: PointerCoordinates) => {
                // Ignore own mouse position
                if (data.id === uuid) {
                    return;
                }
                // Important: Only show cursor if it's in the current room
                if (data.room !== currentPagePath.value) {
                    // If a cursor from another room is received, remove it if it exists
                    if (activeCursorElements.has(data.id)) { // Check our map for existence
                        removePointer(data.id);
                    }
                    return;
                }

                const page = document.body;
                const pageWidth = page?.clientWidth;
                const pageHeight = page?.clientHeight;

                // Ensure pageWidth and pageHeight are valid numbers before calculating
                const drawCoordX = pageWidth ? ((pageWidth / 100) * Number(data.x)).toFixed() : '0';
                const drawCoordY = pageHeight ? ((pageHeight / 100) * Number(data.y)).toFixed() : '0';

                // Ensure cursor is created if it doesn't exist yet (e.g., if userJoinedRoom was missed or late)
                if (!activeCursorElements.has(data.id)) { // Check our map for existence
                    createPointer(data.id, data.name);
                }
                movePointer(data.id, Number(drawCoordX), Number(drawCoordY));
            });

            socketSubscribe('removeUser', (userInfo: { userID: string; userName: string; }) => {
                // Ignore removal of own cursor
                if (userInfo.userID === uuid) {
                    return;
                }
                // Assume removeUser implies leaving the current room, or a global disconnect
                removePointer(userInfo.userID);
            });
        }
    };

    // Disconnect socket connection and remove event listeners
    const disconnectSocket = () => {
        if (socket.value) {
            socket.value.disconnect();
            socket.value = null;
            window.removeEventListener('mousemove', handleMouseMove);
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            lastMousePos = null;
            lastSentTime = 0;

            // Remove all cursors from DOM and map
            activeCursorElements.forEach((element, userID) => {
                element.remove();
            });
            activeCursorElements.clear(); // Clear the map
            loggedUsers.value = []; // Clear the list of logged in users
        }
    };

    // Check if the device is a touch device
    const isTouchDevice = () => {
        return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
    };

    // Observe functionalCookies to control the socket connection
    watch(functionalCookies, (newVal) => {
        if (process.client) {
            if (newVal === true && !isTouchDevice()) {
                connectSocket();
            } else if (newVal === false) {
                disconnectSocket();
            }
        }
    }, { immediate: true }); // 'immediate: true' to run the watcher immediately on component mount

    // Watch for changes in the current page path (room)
    watch(currentPagePath, (newPath, oldPath) => {
        if (process.client && socket.value?.connected) {
            // When the room changes, clear all existing cursors from DOM and map
            activeCursorElements.forEach((element, userID) => {
                element.remove();
            });
            activeCursorElements.clear(); // Clear the map
            loggedUsers.value = []; // Clear the list of logged in users

            // Re-send init with new room, to inform server and other clients in new room
            let name = visitorName.value === '' ? 'UNKNOWN-USER' : visitorName.value.toUpperCase();
            socketEmit("init", {
                uuid: uuid,
                name: name,
                role: "master",
                room: newPath // Send updated room
            });
        }
    });

    onUnmounted(() => {
        disconnectSocket();
    });

    return {
        loggedUsers,
        uuid, // Expose own UUID if needed
    };
}